import { useEffect } from "react";
import type { JSX } from "react";
import { AppRuntimeProvider } from "./providers/AppRuntimeProvider";
import { RouterProvider } from "./providers/RouterProvider";
import { WebSocketProvider } from "./providers/WebSocketProvider";
import { AnalyticsAppEvents, AnalyticsProvider } from "./analytics/provider";
import { useWebSocket } from "./hooks/useWebSocket";
import { AppLayout } from "./layout/AppLayout";
import { APP_MODULES } from "./modules";
import { useUiStore } from "./store/uiStore";
import { LoginGate } from "./pages/LoginGate";
import { useAuthStore } from "./store/authStore";
import { useWorkspaceStore } from "./store/workspaceStore";
import { SetupGate } from "./modules/setup/SetupGate";

function AppContent(): JSX.Element {
  const { backendReady, status } = useWebSocket();
  const setBackendReady = useUiStore((s) => s.setBackendReady);
  const theme = useUiStore((s) => s.theme);

  useEffect(() => {
    setBackendReady(backendReady);
  }, [backendReady, setBackendReady]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function applyTheme(mode: "dark" | "light" | "system") {
      let resolved: "dark" | "light";
      if (mode === "system") {
        resolved = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      } else {
        resolved = mode;
      }
      document.documentElement.dataset.theme = resolved;
    }

    applyTheme(theme);

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handler = () => { applyTheme("system"); };
    if (theme === "system") {
      mq.addEventListener("change", handler);
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { mq.removeEventListener("change", handler); };
  }, [theme]);

  return (
    <AppRuntimeProvider moduleDefinitions={APP_MODULES}>
      <SetupGate>
      <AnalyticsProvider>
        <RouterProvider>
          <AnalyticsAppEvents />
          <AppLayout
            fallback={
              <div className="flex items-center justify-center h-full text-content-tertiary text-base">
                {status === "Initializing..." ? status : "Module not found"}
              </div>
            }
          />
        </RouterProvider>
      </AnalyticsProvider>
      </SetupGate>
    </AppRuntimeProvider>
  );
}

export function App(): JSX.Element {
  const ready = useWorkspaceStore((state) => state.ready);
  const initialize = useWorkspaceStore((state) => state.initialize);
  const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);
  const authenticatedUserId = useAuthStore((state) => state.user?.id ?? null);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary text-content-secondary">
        Loading workspace...
      </div>
    );
  }

  // @tested-by: tst_fe_app_workspace_001, tst_fe_app_workspace_002
  // The runtime owns user-scoped query caches and the authenticated socket.
  // Reusing it across Google accounts can display the previous account's
  // cached empty state even after the new tenant has finished seeding.
  const workspaceBoundaryKey =
    `${selectedWorkspaceId ?? "no-workspace"}:${authenticatedUserId ?? "anonymous"}`;

  return (
    <WebSocketProvider key={workspaceBoundaryKey}>
      <LoginGate>
        <AppContent />
      </LoginGate>
    </WebSocketProvider>
  );
}
